import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Ingredient } from '../types/Types';

/**
 * Archive-view data for /os/archive.
 *
 * Extracted out of screens/OS.tsx: the screen had these two GETs inline, which broke the
 * "API calls never inline in a screen — always in hooks/useXxx.ts" rule and made the screen
 * own token handling and loading flags it has no business owning.
 *
 * The archive view deliberately shows ONLY the single most-recently-archived normativ
 * (globally). On an archived record `updatedAt` IS the archive time, so `sort=updatedAt desc`
 * with `limit=1` is exactly "the last one archived" — there is no picker list.
 *
 * Inert unless `archiveView` is true, so the live /os screen can call it unconditionally
 * without firing a request.
 */
export const useOSArchive = ({ archiveView = false }: { archiveView?: boolean } = {}) => {
  const [archiveIngredients, setArchiveIngredients] = useState<Ingredient[]>([]);
  const [loadingArchive, setLoadingArchive] = useState<boolean>(false);
  const [selectedArchiveId, setSelectedArchiveId] = useState<string | null>(null);
  const [archiveDetail, setArchiveDetail] = useState<Ingredient | null>(null);
  const [loadingArchiveDetail, setLoadingArchiveDetail] = useState<boolean>(false);

  const apiUrl = process.env.REACT_APP_API_URL ?? '';

  const fetchArchiveIngredients = useCallback(async () => {
    try {
      setLoadingArchive(true);
      const token = localStorage.getItem('token');
      if (!token) return;
      const response = await axios.get<{ ingredients: Ingredient[] }>(`${apiUrl}/api/ingredients`, {
        headers: { 'x-auth-token': token },
        params: {
          tab: 'archive',
          sort: 'updatedAt',
          order: 'desc',
          page: 1,
          limit: 1,
        },
      });
      const rows = response.data.ingredients || [];
      setArchiveIngredients(rows);
      if (rows.length > 0) {
        setSelectedArchiveId(rows[0]._id);
      } else {
        setSelectedArchiveId(null);
        setArchiveDetail(null);
      }
    } catch {
      setArchiveIngredients([]);
    } finally {
      setLoadingArchive(false);
    }
  }, [apiUrl]);

  const fetchArchiveDetail = useCallback(async (archiveId: string) => {
    try {
      setLoadingArchiveDetail(true);
      const token = localStorage.getItem('token');
      if (!token) return;
      const response = await axios.get<{ ingredient: Ingredient; materials: any[] }>(
        `${apiUrl}/api/ingredients/archive/${archiveId}`,
        { headers: { 'x-auth-token': token } }
      );
      setArchiveDetail(response.data.ingredient || null);
    } catch {
      setArchiveDetail(null);
    } finally {
      setLoadingArchiveDetail(false);
    }
  }, [apiUrl]);

  useEffect(() => {
    if (archiveView) fetchArchiveIngredients();
  }, [archiveView, fetchArchiveIngredients]);

  useEffect(() => {
    if (archiveView && selectedArchiveId) fetchArchiveDetail(selectedArchiveId);
  }, [archiveView, selectedArchiveId, fetchArchiveDetail]);

  return {
    archiveIngredients,
    loadingArchive,
    selectedArchiveId,
    /** Set by OSSearch's onSelect in archive view; drives the detail fetch above. */
    setSelectedArchiveId,
    archiveDetail,
    loadingArchiveDetail,
  };
};
